/**
 * folderEntryWalker.ts
 * 
 * Utility for traversing dropped folders using DataTransferItem.webkitGetAsEntry()
 * Recursively collects File objects from nested directories with validation.
 * 
 * Features:
 * - Recursive directory traversal (depth-first)
 * - Feature detection for webkitGetAsEntry
 * - File validation integration
 * - Error handling for large directory trees
 * - Graceful fallback for unsupported entries
 */

import { validateFile, type ValidationResult } from './fileValidator';
import type { UploadOptions } from '../types/s3';

/**
 * Result of folder entry traversal
 */
export interface FolderTraversalResult {
  /** Array of collected File objects */
  files: File[];
  /** Validation result for collected files */
  validationResult: ValidationResult;
  /** Errors encountered during traversal */
  traversalErrors: string[];
  /** Number of directories processed */
  directoriesProcessed: number;
}

/**
 * Configuration for folder traversal
 */
export interface FolderTraversalConfig {
  /** Maximum depth to traverse (default: 50) */
  maxDepth?: number;
  /** Maximum number of files to collect (default: 10000) */
  maxFiles?: number;
  /** Upload options for file validation */
  uploadOptions?: UploadOptions;
  /** Callback for progress updates */
  onProgress?: (message: string) => void;
}

/**
 * Check if entry is a folder/directory
 */
const isDirectory = (entry: FileSystemEntry | undefined): entry is FileSystemDirectoryEntry => {
  return !!entry && (entry as FileSystemDirectoryEntry).isDirectory === true;
};

/**
 * Check if entry is a file
 */
const isFile = (entry: FileSystemEntry | undefined): entry is FileSystemFileEntry => {
  return !!entry && (entry as FileSystemFileEntry).isFile === true;
};

/**
 * Get File object from a FileSystemFileEntry
 */
const getFileFromEntry = (entry: FileSystemFileEntry): Promise<File> => {
  return new Promise((resolve, reject) => {
    entry.file(
      (file: File) => resolve(file),
      (error: DOMException) => reject(error)
    );
  });
};

/**
 * Read directory entries with pagination support
 * DirectoryReader.readEntries returns a limited number of entries per call,
 * so we need to loop until we get an empty result
 */
const readDirectoryEntries = (dirReader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
  return new Promise((resolve, reject) => {
    const entries: FileSystemEntry[] = [];
    
    const readMore = () => {
      dirReader.readEntries(
        (batch: FileSystemEntry[]) => {
          if (batch.length === 0) {
            // No more entries to read
            resolve(entries);
          } else {
            // Add batch to entries and continue reading
            entries.push(...batch);
            readMore(); // Continue reading next batch
          }
        },
        (error: DOMException) => {
          reject(error);
        }
      );
    };
    
    readMore();
  });
};

/**
 * Recursively traverse a directory and collect all files
 */
const traverseDirectoryRecursive = async (
  entry: FileSystemDirectoryEntry,
  files: File[],
  config: FolderTraversalConfig,
  depth: number = 0,
  errors: string[] = [],
  directoriesProcessed: { count: number } = { count: 0 }
): Promise<void> => {
  const maxDepth = config.maxDepth ?? 50;
  const maxFiles = config.maxFiles ?? 10000;
  
  // Check depth limit
  if (depth > maxDepth) {
    errors.push(`Maximum depth (${maxDepth}) exceeded while traversing directories`);
    return;
  }
  
  // Check file count limit
  if (files.length >= maxFiles) {
    errors.push(`Maximum file count (${maxFiles}) reached during traversal`);
    return;
  }
  
  try {
    directoriesProcessed.count++;
    
    if (config.onProgress) {
      config.onProgress(`Processing directory: ${entry.name || 'root'}`);
    }
    
    const dirReader = entry.createReader();
    const entries = await readDirectoryEntries(dirReader);
    
    // Process entries in order
    for (const childEntry of entries) {
      // Stop if we've reached max files
      if (files.length >= maxFiles) {
        errors.push(`Maximum file count (${maxFiles}) reached during traversal`);
        return;
      }
      
      if (isFile(childEntry)) {
        try {
          const file = await getFileFromEntry(childEntry);
          
          // Validate file if options provided
          if (config.uploadOptions) {
            const validationResult = validateFile(file, config.uploadOptions);
            if (validationResult.isValid) {
              files.push(file);
            } else if (config.onProgress && validationResult.errors.length > 0) {
              config.onProgress(
                `Skipping invalid file: ${file.name} - ${validationResult.errors[0]}`
              );
            }
          } else {
            // No validation, add all files
            files.push(file);
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`Failed to process file: ${childEntry.name} - ${errorMsg}`);
          if (config.onProgress) {
            config.onProgress(`Error: ${errorMsg}`);
          }
        }
      } else if (isDirectory(childEntry)) {
        // Recursively traverse subdirectory
        try {
          await traverseDirectoryRecursive(
            childEntry,
            files,
            config,
            depth + 1,
            errors,
            directoriesProcessed
          );
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`Failed to traverse directory: ${childEntry.name} - ${errorMsg}`);
          if (config.onProgress) {
            config.onProgress(`Error traversing: ${childEntry.name}`);
          }
        }
      }
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    errors.push(`Failed to read directory: ${entry.name} - ${errorMsg}`);
    if (config.onProgress) {
      config.onProgress(`Error reading directory: ${errorMsg}`);
    }
  }
};

/**
 * Traverse dropped folders and collect all files
 * 
 * @param items DataTransferItem array (from drop event)
 * @param config Traversal configuration
 * @returns Promise resolving to folder traversal result with collected files
 */
export const traverseFolderEntries = async (
  items: DataTransferItem[],
  config: FolderTraversalConfig = {}
): Promise<FolderTraversalResult> => {
  const files: File[] = [];
  const traversalErrors: string[] = [];
  let directoriesProcessed = 0;
  
  const maxFiles = config.maxFiles ?? 10000;
  
  // Process each dropped item
  for (const item of items) {
    // Stop if we've reached max files
    if (files.length >= maxFiles) {
      traversalErrors.push(`Maximum file count (${maxFiles}) reached`);
      break;
    }
    
    if (item.kind !== 'file') {
      continue; // Skip non-file items
    }
    
    // Feature detect webkitGetAsEntry
    const entry = (item as any).webkitGetAsEntry?.();
    
    if (!entry) {
      // Fallback: treat as regular file
      const file = item.getAsFile();
      if (file) {
        // Validate file if options provided
        if (config.uploadOptions) {
          const validationResult = validateFile(file, config.uploadOptions);
          if (validationResult.isValid) {
            files.push(file);
          }
        } else {
          files.push(file);
        }
      }
      continue;
    }
    
    if (isDirectory(entry)) {
      // Traverse directory
      try {
        const dirCountTracker = { count: 0 };
        await traverseDirectoryRecursive(
          entry,
          files,
          config,
          0,
          traversalErrors,
          dirCountTracker
        );
        directoriesProcessed += dirCountTracker.count;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        traversalErrors.push(`Failed to traverse folder: ${entry.name} - ${errorMsg}`);
      }
    } else if (isFile(entry)) {
      // Process single file entry
      try {
        const file = await getFileFromEntry(entry);
        
        // Validate file if options provided
        if (config.uploadOptions) {
          const validationResult = validateFile(file, config.uploadOptions);
          if (validationResult.isValid) {
            files.push(file);
          }
        } else {
          files.push(file);
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        traversalErrors.push(`Failed to process file: ${entry.name} - ${errorMsg}`);
      }
    }
  }
  
  // Create validation result
  const validationResult: ValidationResult = {
    isValid: files.length > 0 && traversalErrors.length === 0,
    errors: traversalErrors.length > 0 ? traversalErrors : [],
    warnings: [],
  };
  
  return {
    files,
    validationResult,
    traversalErrors,
    directoriesProcessed,
  };
};

/**
 * Process folder entries and collect files
 * Convenience wrapper for traverseFolderEntries with default config
 * 
 * @param items DataTransferItem array
 * @param uploadOptions Upload validation options
 * @returns Promise resolving to collected files
 */
export const collectFilesFromFolders = async (
  items: DataTransferItem[],
  uploadOptions?: UploadOptions
): Promise<File[]> => {
  const result = await traverseFolderEntries(items, { uploadOptions });
  return result.files;
};

export default traverseFolderEntries;
